import * as logger from "@rentiohq/shared-frontend/dist/logger/logger";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import * as rentRegistrationSelectors from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.selectors";
import { CONTRACT_TEMPLATE_TYPES } from "@rentiohq/shared-frontend/dist/reduxV2/templateDocuments/templateDocument.constants";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import { EDocumentCategory } from "@rentiohq/shared-frontend/dist/types/document.types";
import {
  ERegistrationMemberType,
  ICreateRegistration,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import {
  buildAutoRegistrationCreateBody,
  EAutoRegistrationDisabledReason,
  EAutoRegistrationKind,
  EAutoRegistrationSubmitAction,
  getAutoRegistrationDisabledReason,
  getAutoRegistrationKind,
  getAutoRegistrationSubmitAction,
  getDefaultRegistrationDestination,
  getRegistrationMembersForSigning,
  IAutoRegistrationMember,
} from "@rentiohq/shared-frontend/dist/utils/autoRegistration.utils";
import { getStore } from "@rentiohq/shared-frontend/dist/utils/redux/redux.utils";
import { getDocumentFromStore } from "./schema.createDocumentPackage.6.utils";
import { EField, IExtraData } from "./schema.createDocumentPackage.types";
import {
  getRelatedContract,
  getRelatedProperty,
} from "./schema.createDocumentPackage.utils";

export interface IAutoRegistrationContext {
  /** Which question the wizard asks, if any. Undefined means: no step at all. */
  kind?: EAutoRegistrationKind;
  disabledReason?: EAutoRegistrationDisabledReason;
  isDocumentRecognized: boolean;
  /** The parties as they will be sent to MyRent. */
  members: IAutoRegistrationMember[];
  /**
   * The accounts whose contact data the step shows and gates on. Equal to the
   * members, except that the broker is checked through the legal contact: that
   * is the contact the signing package resolves as the lessor, so it is the one
   * whose address and company number end up on the MyRent payload.
   */
  partyAccountIds: number[];
  contractId?: string;
  propertyId?: number;
  /**
   * The lease's existing registration. The inspection-report variant links this
   * one instead of creating a registration of its own.
   */
  registrationId?: number;
}

/**
 * The lease's registration, for the barcode the inspection-report question is
 * gated on. Undefined while the fetch is in flight, which the disabled reason
 * treats the same as "no barcode" - the opt-in stays no until it is known.
 * DocumentPackageForm is what triggers that fetch.
 */
const getRegistrationFromStore = (registrationId?: number) => {
  if (registrationId == null) {
    return;
  }

  const state = getStore()?.getState();
  if (!state) {
    return;
  }

  return rentRegistrationSelectors.getDetail(state, registrationId);
};

export const getAutoRegistrationContext = (params: {
  formData: { [key: string]: any };
  extraData: IExtraData;
}): IAutoRegistrationContext => {
  const { formData, extraData } = params;

  const contract = getRelatedContract(extraData, formData);
  const property = getRelatedProperty(extraData, formData);
  const sourceDocument = getDocumentFromStore({ formData, extraData });

  const kind = getAutoRegistrationKind({
    documentPackageType: formData[EField.DocumentPackageType],
    contractId: formData[EField.Contract],
    leaseType: contract?.leaseType,
    property,
    sourceDocumentCategoryId: sourceDocument?.categoryId,
    sourceDocumentContractId: sourceDocument?.contractId,
    templateDocumentType: extraData.templateDocument?.type,
  });

  // The broker signs on behalf of the owners when step 6's sign-as-broker
  // option is picked; the owners are then not parties on the registration.
  const signAsBrokerAccountId = formData[EField.SignAsBroker]?.[0];

  const members = contract
    ? getRegistrationMembersForSigning({
        members: contract.members,
        signAsBrokerAccountId:
          signAsBrokerAccountId != null ? +signAsBrokerAccountId : undefined,
      })
    : [];

  const legalContactAccountId = extraData.legalContact?.accountId;

  const templateDocumentType = extraData.templateDocument?.type;

  return {
    kind,
    isDocumentRecognized:
      kind === EAutoRegistrationKind.Pir ||
      sourceDocument?.categoryId === EDocumentCategory.Contract ||
      (templateDocumentType != null &&
        CONTRACT_TEMPLATE_TYPES.includes(templateDocumentType)),
    disabledReason: getAutoRegistrationDisabledReason({
      kind,
      contract,
      registration: getRegistrationFromStore(contract?.rentRegistrationId),
    }),
    members,
    partyAccountIds: members.map(member =>
      member.role === ERegistrationMemberType.Broker &&
      legalContactAccountId != null
        ? legalContactAccountId
        : member.accountId,
    ),
    contractId: contract?.id,
    propertyId: property?.id,
    registrationId: contract?.rentRegistrationId,
  };
};

export const getOptInAnswerKey = (formData: {
  [key: string]: unknown;
}): string | undefined => {
  const documents = formData[EField.Documents];
  const document = Array.isArray(documents) ? documents[0] : undefined;

  if (document == null) {
    return;
  }

  return `${getDocumentKey(document)}|lease:${formData[EField.Contract]}`;
};

const getDocumentKey = (document: unknown) => {
  if (typeof document === "string") {
    return `document:${document}`;
  }

  const { name, size, lastModified } = document as Partial<File>;
  return `upload:${name}:${size}:${lastModified}`;
};

export const getAutoRegistrationOptIn = (params: {
  formData: { [key: string]: unknown };
  extraData: IExtraData;
}): boolean | undefined => {
  const { formData } = params;
  const { kind, disabledReason, isDocumentRecognized } =
    getAutoRegistrationContext(params);

  const answer = formData[EField.AutoRegisterMyRent];
  const isAnsweredForThisDocument =
    formData[EField.AutoRegisterMyRentAnsweredFor] ===
    getOptInAnswerKey(formData);

  if (typeof answer === "boolean" && isAnsweredForThisDocument) {
    return answer;
  }

  if (!kind) {
    return;
  }

  if (disabledReason) {
    return false;
  }

  return isDocumentRecognized;
};

/** Every party contact the step can resolve. A contact that is not in the store
 * yet is left out rather than treated as incomplete, so a slow contact fetch
 * never blocks the wizard - the signing-completion check catches it either way.
 */
export const getRegistrationPartyContacts = (
  accountIds: number[],
): IContact[] => {
  const state = getStore()?.getState();
  if (!state) {
    return [];
  }

  return accountIds
    .map(accountId => contactSelectors.getContact(state, undefined, accountId))
    .filter((contact): contact is IContact => !!contact);
};

/**
 * The prefilled destination, or undefined while the lease is not known yet -
 * the step schema is also evaluated against an empty form at form creation, and
 * a destination derived then would outrank the real one.
 */
export const getDefaultDestinationForForm = (params: {
  formData: { [key: string]: any };
  extraData: IExtraData;
}) => {
  const { formData, extraData } = params;

  const contract = getRelatedContract(extraData, formData);
  if (!contract) {
    return;
  }

  return getDefaultRegistrationDestination({
    leaseType: contract.leaseType,
    propertyTypeId: getRelatedProperty(extraData, formData)?.typeId,
  });
};

/**
 * What the document package has to carry for the broker's opt-in: a registration
 * for the saga to create (the lease contract), or the id of the lease's existing
 * registration to link (the inspection report). Both empty when the broker did
 * not opt in.
 */
export const getAutoRegistrationForSubmit = (params: {
  formData: { [key: string]: any };
  extraData: IExtraData;
}): {
  rentRegistration?: ICreateRegistration;
  rentRegistrationId?: number;
} => {
  const { formData } = params;

  const {
    kind,
    disabledReason,
    members,
    contractId,
    propertyId,
    registrationId,
  } = getAutoRegistrationContext(params);

  const submit = getAutoRegistrationSubmitAction({
    isOptedIn: !!getAutoRegistrationOptIn(params),
    kind,
    disabledReason,
    contractId,
    propertyId,
  });

  switch (submit.action) {
    case EAutoRegistrationSubmitAction.Create:
      return {
        rentRegistration: buildAutoRegistrationCreateBody({
          // Carried by the decision, so the body is built from the lease and
          // property it was actually taken on.
          contractId: submit.contractId,
          propertyId: submit.propertyId,
          members,
          destination: formData[EField.RegistrationDestination],
          language: formData[EField.RegistrationLanguage],
          propertyTaxPaidByTenant: formData[EField.RegistrationTaxPaidByTenant],
        }),
      };

    // The inspection report is attached to the lease's existing registration
    // rather than getting one of its own, so the package only has to carry that
    // registration's id - the same `rentRegistrationId` link the contract
    // variant ends up with once the saga has created its registration. Sending
    // the report to MyRent is the backend's job either way; the barcode gate is
    // what guarantees MyRent can accept it against this registration.
    // `registrationId` is the id the barcode was read through, so it cannot be
    // unset while no disabled reason stands.
    case EAutoRegistrationSubmitAction.LinkExisting:
      return { rentRegistrationId: registrationId };

    case EAutoRegistrationSubmitAction.DropOptIn:
      // The step never offers the opt-in enabled in this state, so the broker
      // opted in and silently got nothing. Leave a trail for it.
      logger.logError({
        error: new Error(
          `Auto-registration opt-in dropped (kind: ${kind}, disabledReason: ${disabledReason}, contractId: ${contractId}, propertyId: ${propertyId}, registrationId: ${registrationId})`,
        ),
      });
      return {};

    default:
      return {};
  }
};
